import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronLeft, Upload, Loader2, CheckCircle2, Clock, XCircle } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCustomerProfile } from "@/hooks/useLocal";
import { Spinner } from "@/components/common/States";
import { cn } from "@/lib/utils";

export default function CommunityDiscount() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { profile } = useCustomerProfile();
  const phone = user?.phone || profile?.phone || "";
  const [name, setName] = useState(user?.name || profile?.name || "");
  const [category, setCategory] = useState(null);
  const [proof, setProof] = useState(null);
  const [uploading, setUploading] = useState(false);

  const catsQuery = useQuery({ queryKey: ["communityCategories"], queryFn: api.getCommunityCategories });
  const statusQuery = useQuery({ queryKey: ["communityStatus", phone], queryFn: () => api.getCommunityStatus(phone), enabled: !!phone });

  const apply = useMutation({
    mutationFn: () => api.applyCommunityDiscount({ phone, customer_name: name.trim(), category, proof_path: proof?.path || "" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["communityStatus"] });
      toast.success("Request submitted — we'll review it shortly");
      setCategory(null);
      setProof(null);
    },
    onError: (e) => toast.error(e.message || "Could not submit"),
  });

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      setProof(await api.uploadPhoto(file));
      toast.success("Proof attached");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const latest = statusQuery.data?.latest_request;
  const statusMeta = { pending: { icon: Clock, tint: "text-[hsl(var(--warning))]", label: "Under review" }, approved: { icon: CheckCircle2, tint: "text-[hsl(var(--success))]", label: "Approved" }, rejected: { icon: XCircle, tint: "text-destructive", label: "Not approved" } };

  return (
    <div className="mx-auto max-w-xl space-y-5 py-4">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate(-1)} className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-secondary"><ChevronLeft className="h-6 w-6" /></button>
        <h1 className="font-display text-xl font-bold">Community discount</h1>
      </div>

      {!phone ? (
        <p className="rounded-xl bg-secondary p-4 text-sm text-muted-foreground">Add your phone number in your profile to apply.</p>
      ) : statusQuery.data?.approved_category ? (
        <div className="rounded-2xl bg-[hsl(var(--brand-tertiary))] p-5">
          <p className="flex items-center gap-2 font-semibold text-primary"><CheckCircle2 className="h-5 w-5" /> You're approved as {statusQuery.data.approved_category_label}</p>
          <p className="mt-1 text-sm text-foreground/70">Your community benefit applies automatically at checkout.</p>
        </div>
      ) : latest && latest.status === "pending" ? (
        <div className="rounded-2xl border border-border p-5">
          <p className="flex items-center gap-2 font-semibold text-[hsl(var(--warning))]"><Clock className="h-5 w-5" /> Request under review</p>
          <p className="mt-1 text-sm text-muted-foreground">We're verifying your proof. You'll be notified once reviewed.</p>
        </div>
      ) : (
        <>
          {latest && latest.status === "rejected" ? <p className="rounded-xl bg-destructive/10 p-3 text-sm text-destructive">Your previous request wasn't approved{latest.reject_reason ? `: ${latest.reject_reason}` : ""}. You can re-apply below.</p> : null}
          <p className="text-sm text-muted-foreground">Students, teachers, residents, medical & defense personnel and local businesses get special benefits. Pick your category and upload proof.</p>

          <div>
            <label className="text-sm font-semibold">Your name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full rounded-xl border border-border bg-card p-3 text-sm outline-none focus:border-primary" />
          </div>

          {catsQuery.isPending ? <Spinner /> : (
            <div className="space-y-2">
              {(catsQuery.data?.categories ?? []).map((c) => (
                <button key={c.key} onClick={() => setCategory(c.key)} className={cn("w-full rounded-xl border p-3 text-left", category === c.key ? "border-primary bg-[hsl(var(--brand-tertiary))]" : "border-border")}>
                  <p className="text-sm font-semibold">{c.label}</p>
                  <p className="text-xs text-muted-foreground">{c.proof_hint}{c.free_kg_per_week ? ` · ${c.free_kg_per_week} free kg/week` : ""}</p>
                </button>
              ))}
            </div>
          )}

          <div>
            <label className="text-sm font-semibold">Proof of eligibility</label>
            <div className="mt-2 flex items-center gap-3">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-primary px-4 py-2 text-sm font-semibold text-primary">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
                <input type="file" accept="image/*" className="hidden" onChange={onUpload} />
              </label>
              {proof ? <img src={fileUrl(proof.path)} alt="proof" className="h-14 w-14 rounded-lg border border-border object-cover" /> : null}
            </div>
          </div>

          <button onClick={() => apply.mutate()} disabled={!category || !proof || name.trim().length < 2 || apply.isPending} data-testid="community-submit" className="w-full rounded-full bg-primary py-3 font-semibold text-primary-foreground disabled:opacity-50">
            {apply.isPending ? "Submitting…" : "Submit request"}
          </button>
        </>
      )}
    </div>
  );
}
